import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ExternalLink, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useAddLink } from '@/hooks/useAddLink';
import { isValidUrl, extractDomain } from '@readlater/shared';
import type { ContentFormat } from '@readlater/shared';
import { trackCriticalAction, trackEvent } from '@/lib/analytics';

type SaveState =
  | 'loading'
  | 'waiting-content'
  | 'saving'
  | 'success'
  | 'error'
  | 'invalid'
  | 'login';

interface ContentMessage {
  type: 'readlater-content';
  content: string;
  contentFormat?: ContentFormat;
  title: string;
  url: string;
}

// Cap accepted content at 1 MB — well above any realistic article size,
// but prevents a malicious or broken bookmarklet from flooding memory.
const MAX_BOOKMARKLET_CONTENT_LENGTH = 1_000_000;

function isContentFormat(value: unknown): value is ContentFormat {
  return value === 'html' || value === 'text' || value === 'markdown';
}

function getUrlOrigin(value: string | null): string | null {
  if (!value) return null;
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

export function Save() {
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<SaveState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [receivedContent, setReceivedContent] = useState<ContentMessage | null>(null);
  const saveStartTime = useRef<number>(0);
  const hasSaved = useRef(false);

  const url = searchParams.get('url') || searchParams.get('text');
  const title = searchParams.get('title');
  const mode = searchParams.get('mode'); // 'html' or 'content' for advanced bookmarklet
  const isContentMode = mode === 'content' || mode === 'html';
  const expectedSourceOrigin = getUrlOrigin(url);

  // Unified hook for all saves (URL only or with content)
  const addLink = useAddLink({
    onSuccess: (link) => {
      const duration = Date.now() - saveStartTime.current;
      trackCriticalAction('bookmarklet_save', true, {
        linkId: link.id,
        durationMs: duration,
        mode: isContentMode ? 'content' : 'basic',
      });
      setState('success');
      setTimeout(() => window.close(), 2000);
    },
    onError: (error) => {
      const duration = Date.now() - saveStartTime.current;
      trackCriticalAction('bookmarklet_save', false, {
        error: error instanceof Error ? error.message : 'Failed to save link',
        durationMs: duration,
        mode: isContentMode ? 'content' : 'basic',
      });
      setState('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save link');
    },
  });

  // Handle message from bookmarklet with extracted content
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data?.type !== 'readlater-content' || hasSaved.current) return;

      // Only accept messages from the opener window (prevents unrelated window injection)
      if (!window.opener || event.source !== window.opener) return;

      const payload = event.data as Partial<ContentMessage>;

      if (
        typeof payload.url !== 'string' ||
        typeof payload.title !== 'string' ||
        typeof payload.content !== 'string'
      ) {
        return;
      }

      if (payload.content.length < 1 || payload.content.length > MAX_BOOKMARKLET_CONTENT_LENGTH) {
        return;
      }

      // The sender's origin must match the URL being saved.
      // This prevents cross-origin pages from injecting content for arbitrary URLs.
      const payloadOrigin = getUrlOrigin(payload.url);
      if (!payloadOrigin || event.origin !== payloadOrigin) return;

      // If URL is present in query params, require exact match with the posted URL.
      if (url && payload.url !== url) return;

      // Defense-in-depth: also pin origin against the URL query param.
      // Redundant when payload.url matches the query param (line 111 + 108 imply this),
      // but guards against future refactors that might relax the exact-URL check above.
      if (expectedSourceOrigin && event.origin !== expectedSourceOrigin) return;

      setReceivedContent({
        type: 'readlater-content',
        url: payload.url,
        title: payload.title,
        content: payload.content,
        contentFormat: isContentFormat(payload.contentFormat) ? payload.contentFormat : 'text',
      });
    },
    [expectedSourceOrigin, url]
  );

  // Listen for postMessage from bookmarklet
  useEffect(() => {
    if (isContentMode) {
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, [isContentMode, handleMessage]);

  // Save with content once received
  useEffect(() => {
    if (receivedContent && user && !hasSaved.current && state === 'waiting-content') {
      hasSaved.current = true;
      saveStartTime.current = Date.now();
      setState('saving');
      // Use consolidated API with content and contentFormat
      addLink.mutate({
        url: receivedContent.url,
        title: receivedContent.title,
        content: receivedContent.content,
        contentFormat: receivedContent.contentFormat || 'text',
      });
    }
  }, [receivedContent, user, state, addLink]);

  // Main effect for initiating save
  useEffect(() => {
    if (authLoading) {
      setState('loading');
      return;
    }

    if (!user) {
      trackEvent('bookmarklet.login_required' as never, { url });
      setState('login');
      return;
    }

    if (!url || !isValidUrl(url)) {
      trackEvent('bookmarklet.invalid_url' as never, { url });
      setState('invalid');
      return;
    }

    // For content mode, wait for postMessage with content
    if (isContentMode) {
      if (!receivedContent && !hasSaved.current) {
        setState('waiting-content');
      }
      return;
    }

    // Basic mode: save immediately
    if (!hasSaved.current) {
      hasSaved.current = true;
      saveStartTime.current = Date.now();
      setState('saving');
      addLink.mutate({ url });
    }
  }, [authLoading, user, url, isContentMode, receivedContent, addLink]);

  const domain = url ? extractDomain(url) : null;

  const handleRetry = () => {
    hasSaved.current = false;
    if (isContentMode && receivedContent) {
      setState('waiting-content');
    } else if (url) {
      saveStartTime.current = Date.now();
      setState('saving');
      addLink.mutate({ url });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2 text-xl">
            {isContentMode && <Zap className="h-5 w-5 text-primary" />}
            Save to Pith
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          {/* Loading state */}
          {state === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading...</p>
            </>
          )}

          {/* Waiting for content from bookmarklet */}
          {state === 'waiting-content' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">Receiving content...</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Extracting page content from bookmarklet
                </p>
              </div>
            </>
          )}

          {/* Saving state */}
          {state === 'saving' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">
                  {isContentMode ? 'Saving with content...' : 'Saving link...'}
                </p>
                {(title || domain) && (
                  <p className="mt-1 max-w-xs truncate text-sm text-muted-foreground">
                    {title || domain}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Success state */}
          {state === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 text-green-500" />
              <div className="text-center">
                <p className="font-medium">Saved!</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  This window will close automatically...
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.close()}>
                Close
              </Button>
            </>
          )}

          {/* Error state */}
          {state === 'error' && (
            <>
              <XCircle className="h-12 w-12 text-destructive" />
              <div className="text-center">
                <p className="font-medium">Failed to save</p>
                <p className="mt-1 text-sm text-muted-foreground">{errorMessage}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => window.close()}>
                  Close
                </Button>
                <Button size="sm" onClick={handleRetry}>
                  Try Again
                </Button>
              </div>
            </>
          )}

          {/* Invalid URL state */}
          {state === 'invalid' && (
            <>
              <XCircle className="h-12 w-12 text-destructive" />
              <div className="text-center">
                <p className="font-medium">Invalid URL</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {url ? 'The provided URL is not valid.' : 'No URL was provided.'}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.close()}>
                Close
              </Button>
            </>
          )}

          {/* Login required state */}
          {state === 'login' && (
            <>
              <div className="rounded-full bg-muted p-3">
                <ExternalLink className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="font-medium">Login required</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Please log in to save links to Pith.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => window.close()}>
                  Close
                </Button>
                <Button size="sm" asChild>
                  <Link
                    to={`/login?redirect=${encodeURIComponent(`/save?url=${encodeURIComponent(url || '')}${mode ? `&mode=${mode}` : ''}`)}`}
                  >
                    Log In
                  </Link>
                </Button>
              </div>
            </>
          )}

          {/* URL preview */}
          {url && state !== 'invalid' && (
            <div className="mt-4 w-full rounded-md bg-muted p-3">
              <p className="truncate text-xs text-muted-foreground">{url}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
